import { type KeyboardEvent, useCallback, useEffect, useRef, useState } from "react";

import { useOutsideClickDetection } from "common/utilities";

type UseAutocompleteParams = {
  onChange: (value: string) => void;
  onClear?: () => void;
  onEndReached?: () => void;
};

export const useAutocomplete = ({ onChange, onClear, onEndReached }: UseAutocompleteParams) => {
  const [areOptionsVisible, setAreOptionsVisible] = useState<boolean>(false);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const resultsContainerRef = useRef<HTMLDivElement | null>(null);
  const bottomOfSearchResultsRef = useRef<HTMLDivElement | null>(null);
  const termLengthRef = useRef<number>(0);

  const updateValueAndShowResults = (newValue: string) => {
    onChange(newValue);
    setAreOptionsVisible(true);
    termLengthRef.current = newValue.length;
  };

  const clearValueAndHideResults = () => {
    onClear?.();
    setAreOptionsVisible(false);
    termLengthRef.current = 0;
  };

  useOutsideClickDetection(resultsContainerRef, () => setAreOptionsVisible(false));

  const handleIntersection = useCallback(
    (entry: IntersectionObserverEntry) => {
      if (entry.isIntersecting) {
        onEndReached?.();
      }
    },
    [onEndReached],
  );

  useEffect(() => {
    const element = bottomOfSearchResultsRef.current;
    if (!element || !areOptionsVisible) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        handleIntersection(entry);
      },
      { threshold: 1 },
    );

    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, [handleIntersection, areOptionsVisible]);

  const openOrCloseResults = (event: KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      setAreOptionsVisible(true);
    } else if (event.key === "Escape") {
      event.preventDefault();
      setAreOptionsVisible(false);
    }
  };

  const closeResults = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      event.preventDefault();
      setAreOptionsVisible(false);
    }
  };

  return {
    containerRef,
    resultsContainerRef,
    bottomOfSearchResultsRef,
    areOptionsVisible,
    setAreOptionsVisible,
    updateValueAndShowResults,
    clearValueAndHideResults,
    openOrCloseResults,
    closeResults,
  };
};
